import type {
  AdventureMap,
  PlacePreviewFeature,
  PlaceGeoJSON,
  PlaceImage,
  RelatedPlace,
  UpdatePlaceData,
} from "~/lib/api";
import { useFeatureFlag } from "~/hooks/useFeatureFlag";
import { routes } from "~/lib/routes";
import { AdventureMapView } from "~/components/adventure-map";
import type { DetailLevel } from "~/lib/detailLevel";
import { PlaceDetailView } from "~/components/place/PlaceDetailView";
import PlaceList from "~/components/place/PlaceList";
import { PanelSection } from "~/components/ui/panel-section";

interface EditorPanelContentProps {
  map: AdventureMap;
  adventureMapPlaces: PlaceGeoJSON;
  selectedPlaceId: string | null;
  /** Place from the global database or preview from Mapbox POI */
  selectedPlace: PlacePreviewFeature | null;
  /** Controls how much content is shown based on available space */
  details?: DetailLevel;
  /** When true, focuses the place detail view (for keyboard navigation) */
  autoFocusPlace?: boolean;
  onPlaceDeselect: () => void;
  onMapUpdate: (data: { name?: string; description?: string | null }) => Promise<void>;
  onShare?: () => void;
  onCollaborators?: () => void;
  onTransfer?: () => void;
  onPlaceUpdate: (data: UpdatePlaceData) => Promise<void>;
  onPlaceDelete: () => Promise<void>;
  onPlacePublish: () => Promise<void>;
  onPlaceTransfer?: () => void;
  onPlaceAdd: () => Promise<void>;
  onPlaceImageAdded: (image: PlaceImage) => void;
  onPlaceImageDeleted: (imageId: string) => void;
  onPlaceTrackUploaded?: (track: GeoJSON.LineString) => void;
  onPlaceTrackDeleted?: () => void;
  onRelatedPlaceRemove?: (relatedPlaceId: string) => Promise<void>;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
  canPublishLandingPage?: boolean;
  onAccessRevoked?: () => void;
}

/**
 * Content for the editor panel (desktop collapsible panel / mobile bottom sheet).
 * Handles drill-down navigation: map overview → place details.
 */
export function EditorPanelContent({
  map,
  adventureMapPlaces,
  selectedPlaceId,
  selectedPlace,
  details = "full",
  autoFocusPlace,
  onPlaceDeselect,
  onMapUpdate,
  onShare,
  onCollaborators,
  onTransfer,
  onPlaceUpdate,
  onPlaceDelete,
  onPlacePublish,
  onPlaceTransfer,
  onPlaceAdd,
  onPlaceImageAdded,
  onPlaceImageDeleted,
  onPlaceTrackUploaded,
  onPlaceTrackDeleted,
  onRelatedPlaceRemove,
  onSelectRelatedPlace,
  canPublishLandingPage,
  onAccessRevoked,
}: EditorPanelContentProps) {
  // Find the selected adventure map place if any
  const selectedAdventureMapPlace = selectedPlaceId
    ? adventureMapPlaces.features.find((f) => f.id === selectedPlaceId)
    : null;

  // Determine which place to show (adventure map place takes precedence)
  const placeToShow = selectedAdventureMapPlace ?? selectedPlace;
  const isAdventureMapPlace = !!selectedAdventureMapPlace;
  const canEdit = placeToShow?.properties.permissions?.canEdit;
  const canPublishFeature = useFeatureFlag("place:publish");
  const canPublish = placeToShow?.properties.permissions?.canPublish && canPublishFeature;
  const canTransfer = placeToShow?.properties.permissions?.canTransfer;
  const canEditRelatedPlaces = isAdventureMapPlace && map.permissions.canEdit;

  if (placeToShow) {
    return (
      <PlaceDetailView
        place={placeToShow.properties}
        onBack={onPlaceDeselect}
        details={details}
        autoFocus={autoFocusPlace}
        onAdd={isAdventureMapPlace ? undefined : onPlaceAdd}
        onUpdate={canEdit ? onPlaceUpdate : undefined}
        onDelete={isAdventureMapPlace ? onPlaceDelete : undefined}
        onPublish={canPublish ? onPlacePublish : undefined}
        onTransfer={canTransfer ? onPlaceTransfer : undefined}
        onImageAdded={canEdit ? onPlaceImageAdded : undefined}
        onImageDeleted={canEdit ? onPlaceImageDeleted : undefined}
        onTrackUploaded={canEdit ? onPlaceTrackUploaded : undefined}
        onTrackDeleted={canEdit ? onPlaceTrackDeleted : undefined}
        onRelatedPlaceRemove={canEditRelatedPlaces ? onRelatedPlaceRemove : undefined}
        onSelectRelatedPlace={onSelectRelatedPlace}
        onAccessRevoked={canEdit ? onAccessRevoked : undefined}
        canEditRelatedPlaces={canEditRelatedPlaces}
      />
    );
  }

  // Default: show map overview with place list
  return (
    <AdventureMapView
      map={map}
      backTo={routes.to.maps.root()}
      onUpdate={onMapUpdate}
      onShare={onShare}
      onCollaborators={onCollaborators}
      onTransfer={onTransfer}
      canPublishLandingPage={canPublishLandingPage}
    >
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Places</PanelSection.Title>
        <PanelSection.Content className="px-0">
          <PlaceList places={adventureMapPlaces.features} selectedId={selectedPlaceId} />
        </PanelSection.Content>
      </PanelSection.Root>
    </AdventureMapView>
  );
}
